import { safe } from "pb.safe";

import {
	$,
	mutation,
	query,
} from "../../../../schema.dev-sdk-api-appsync.generated";
import * as Auth from "../../../@lib-auth/auth";
import { logger } from "../../../shared/logger";
import type { Route } from "./route";
import type { ApiClient } from "./shared/api-client";

export async function logout({
	apiClient,
	request,
}: {
	apiClient: ApiClient;
	request: Route.Request;
}) {
	await unpair(apiClient, request);
	const tokens = await request.tokens();
	return Auth.logout({ headers: { authIdToken: tokens?.authIdToken } });
}

async function unpair(
	apiClient: ApiClient,
	request: {
		headers?: {
			deviceId?: string;
			deviceModel?: string;
			deviceSerial?: string;
		};
	},
): Promise<void> {
	const deviceId = await (async () => {
		const { deviceId } = request.headers ?? {};
		if (deviceId) {
			return deviceId;
		}

		const { deviceModel, deviceSerial } = request.headers ?? {};
		if (!(deviceModel && deviceSerial)) {
			const error = new Error("Missing deviceModel and/or deviceSerial.");
			logger.error("logout: missing device identifiers", {
				errorMessage: error.message,
			});
			return error;
		}

		return await getDeviceId({
			apiClient,
			deviceModel,
			deviceSerial,
		});
	})();

	if (deviceId instanceof Error) {
		return;
	}

	const entityId = await getDeviceEntityId({
		apiClient,
		deviceId,
	});
	if (entityId instanceof Error) {
		return;
	}

	await unpairDeviceFromEntity({
		apiClient,
		deviceId,
		entityId,
	});
}

async function getDeviceId(args: {
	apiClient: ApiClient;
	deviceModel: string;
	deviceSerial: string;
}): Promise<string | Error> {
	const response = await safe(() =>
		args.apiClient.request(getDeviceId.gql.getDeviceUuid, {
			deviceModel: args.deviceModel,
			deviceSerial: args.deviceSerial,
		}),
	);

	if (response instanceof Error) {
		return response;
	}

	return response.getDeviceUuid.id;
}

getDeviceId.gql = {
	getDeviceUuid: mutation((q) => [
		q.getDeviceUuid(
			{
				input: {
					model: $("deviceModel"),
					serial: $("deviceSerial"),
				},
			},
			(c) => [c.id],
		),
	]),
};

async function getDeviceEntityId(args: {
	apiClient: ApiClient;
	deviceId: string;
}): Promise<string | Error> {
	const response = await safe(() =>
		args.apiClient.request(getDeviceEntityId.gql.getDeviceSettings, {
			deviceId: args.deviceId,
		}),
	);

	if (response instanceof Error) {
		return response;
	}

	return response.getDeviceSettings.entityUuid;
}

getDeviceEntityId.gql = {
	getDeviceSettings: query("getDeviceSettings", (q) => [
		q.getDeviceSettings({ deviceUuid: $("deviceId") }, (c) => [c.entityUuid]),
	]),
};

async function unpairDeviceFromEntity(args: {
	apiClient: ApiClient;
	deviceId: string;
	entityId: string;
}): Promise<void | Error> {
	const response = await safe(() =>
		args.apiClient.request(unpairDeviceFromEntity.gql.removeDeviceFromEntity, {
			deviceId: args.deviceId,
			entityId: args.entityId,
		}),
	);

	if (response instanceof Error) {
		return response;
	}

	return undefined;
}

unpairDeviceFromEntity.gql = {
	removeDeviceFromEntity: mutation("removeDeviceFromEntity", (q) => [
		q.removeDeviceFromEntity({
			deviceUuid: $("deviceId"),
			entityUuid: $("entityId"),
		}),
	]),
};
